import axios from 'axios';

/**
 * Structured error item returned by the platform API.
 *
 * @example
 * ```json
 * { "code": "17", "message": "User already has a registered password." }
 * ```
 */
export interface ApiErrorItem {
  /** Numeric error code as string */
  code: string;
  /** Human-readable error description */
  message: string;
}

/**
 * Shape of a platform 4xx/5xx JSON body.
 *
 * @example
 * ```json
 * {
 *   "statusCode": 400,
 *   "message": [{ "code": "17", "message": "User already has a registered password." }],
 *   "timestamp": "2026-06-12T14:49:55.969Z"
 * }
 * ```
 */
interface ApiErrorBody {
  statusCode?: number;
  message?: ApiErrorItem[];
  timestamp?: string;
}

/**
 * Extracts structured error items from an Axios error response.
 *
 * Returns the `message` array from the API body when present,
 * otherwise returns an empty array.
 *
 * @param error - The caught error (typically an AxiosError)
 * @returns Array of API error items, empty if not an Axios error or body is missing
 *
 * @example
 * ```ts
 * const items = getApiErrorItems(error);
 * const hasPasswordError = items.some(e => e.code === '17');
 * ```
 */
export function getApiErrorItems(error: unknown): ApiErrorItem[] {
  if (!axios.isAxiosError(error)) return [];

  const body = error.response?.data as ApiErrorBody | undefined;
  if (!body?.message || !Array.isArray(body.message)) return [];

  return body.message;
}

/**
 * Checks whether an API error contains a specific error code.
 *
 * @param error - The caught error
 * @param code  - The error code to look for (e.g. "17")
 * @returns `true` when the API response body includes the given code
 *
 * @example
 * ```ts
 * if (hasApiErrorCode(error, '17')) {
 *   // User already has a registered password
 * }
 * ```
 */
export function hasApiErrorCode(error: unknown, code: string): boolean {
  return getApiErrorItems(error).some((item) => item.code === code);
}

/**
 * Body of the 403 answered by the auth-bridge when a federated user tries an
 * own-credential flow (password login, password reset/creation, identify).
 *
 * @example
 * ```json
 * {
 *   "statusCode": 403,
 *   "error": "Forbidden",
 *   "code": "FEDERATED_LOGIN_REQUIRED",
 *   "message": "This organization uses corporate (federated) sign-in.",
 *   "registrationId": "bancobv",
 *   "authenticationUrl": "https://.../saml2/authenticate/bancobv"
 * }
 * ```
 */
export interface FederatedLoginRequiredBody {
  statusCode: number;
  error?: string;
  code: 'FEDERATED_LOGIN_REQUIRED';
  message?: string;
  /** IAM-SAML registrationId to start the corporate flow in one action */
  registrationId?: string;
  /** Full IAM-SAML authenticate URL, when configured on the backend */
  authenticationUrl?: string;
}

/**
 * Extracts the `FEDERATED_LOGIN_REQUIRED` body from an Axios error, when the
 * API refused an own-credential flow because the user is federated (RN03/RN04).
 *
 * @param error - The caught error (typically an AxiosError)
 * @returns The structured body, or `null` when this is not a federated block
 *
 * @example
 * ```ts
 * const federated = getFederatedLoginRequired(error);
 * if (federated) navigate('/sso', { state: { email } });
 * ```
 */
export function getFederatedLoginRequired(
  error: unknown
): FederatedLoginRequiredBody | null {
  return readSemanticErrorBody(
    error,
    'FEDERATED_LOGIN_REQUIRED'
  ) as FederatedLoginRequiredBody | null;
}

/**
 * RN05/CA11 — o usuário autenticou com sucesso no IdP da empresa, mas não tem
 * acesso concedido à plataforma (perfil e vínculo não provisionados, RN06/P2).
 *
 * Distinto de {@link getFederatedLoginRequired}: lá o problema é a forma de
 * acesso e a saída é o fluxo corporativo; aqui a autenticação já funcionou e
 * quem resolve é a gestão de acessos da empresa do usuário. Confundir os dois
 * manda o usuário ao canal errado.
 *
 * @param error - The caught error (typically an AxiosError)
 * @returns `true` when the API denied access for lack of authorization
 */
export function isFederatedAccessNotGranted(error: unknown): boolean {
  return readSemanticErrorBody(error, 'FEDERATED_ACCESS_NOT_GRANTED') !== null;
}

/**
 * Lê um corpo de erro semântico do BFF pelo seu `code`, aceitando as duas
 * formas em que ele chega: no topo (filtro global do BFF) ou aninhado em
 * `message` (envelopes de erro que encapsulam a resposta original — gateway).
 * Sem a forma aninhada, o bloqueio viraria um erro genérico e o usuário nunca
 * veria a tela certa.
 */
function readSemanticErrorBody(
  error: unknown,
  code: string
): Record<string, unknown> | null {
  if (!axios.isAxiosError(error)) return null;

  const data = error.response?.data as
    | (Record<string, unknown> & { code?: unknown; message?: unknown })
    | undefined;

  if (data?.code === code) {
    return data;
  }

  const nested = data?.message;
  if (
    nested &&
    typeof nested === 'object' &&
    !Array.isArray(nested) &&
    (nested as { code?: unknown }).code === code
  ) {
    return nested as Record<string, unknown>;
  }

  return null;
}
